/* eslint-disable no-param-reassign */
import _ from 'underscore';
import type { WrappedRequestor } from '../wrapped-requestor';
import { getTimestamp } from '../../utils/primitive-utilities';
import { hasHttpErrorDetails } from '../../utils/type-guards';
import type { InstrumentationContext } from './instrumentation-context';

export interface ErrorMapper {
  check(_error: unknown): unknown;
}

export interface ErrorDetectionResult {
  pass: boolean;
  status: number;
  message?: string;
  impactUser?: boolean;
}

export interface InstrumentationStubOptions {
  serverErrorCodes: number[];
  lcid?: number;
  detectError(_response: unknown, _serverErrorCodes?: number[]): ErrorDetectionResult;
  getServerPerf(_response: unknown): string;
}

export interface PerformanceDetails {
  requestId: string;
  api: string;
  isMethodEnter: boolean;
  httpMethod: string;
  timeTaken: number;
  pass?: boolean;
  message?: string;
  statusCode?: number;
}

export interface ErrorDetails {
  message?: string;
  api: string;
  requestId: string;
  impactUser?: boolean;
  httpMethod: string;
  statusCode?: number;
}

export type PerformanceLogger = (_details: PerformanceDetails) => void;
export type ErrorLogger = (_details: ErrorDetails) => void;
export type TraceLogger = ErrorLogger;

interface ServerErrorEnvelope {
  Errors?: Array<{ Code?: number; Message?: string }>;
}

function isServerErrorEnvelope(value: unknown): value is ServerErrorEnvelope {
  return typeof value === 'object' && value !== null;
}

interface HeaderResponse {
  getResponseHeader(_name: string): string | null;
}

function hasResponseHeaders(value: unknown): value is HeaderResponse {
  return typeof value === 'object'
    && value !== null
    && 'getResponseHeader' in value
    && typeof value.getResponseHeader === 'function';
}

const errorToString = (error: unknown) => {
  const stringError = error && String(error);
  const stringifiedJson = JSON.stringify(error);
  if ((!_.isEmpty(stringifiedJson) && stringifiedJson !== '{}') || stringError === '[object Object]') {
    return stringifiedJson;
  }
  return stringError;
};

export const defaultStubOptions: InstrumentationStubOptions = {
  serverErrorCodes: [-1],
  detectError(response, serverErrorCodes = [-1]) {
    const result: ErrorDetectionResult = { pass: true, status: 400 };
    if (isServerErrorEnvelope(response)) {
      const [firstError] = response.Errors || [];
      if (firstError?.Code && _.contains(serverErrorCodes, firstError.Code)) {
        result.pass = false;
        result.message = firstError.Message;
        result.impactUser = true;
      }
    }
    return result;
  },
  getServerPerf(response) {
    if (!hasResponseHeaders(response)) {
      return '';
    }
    const perfTimings = response.getResponseHeader('PerfTimings');
    if (perfTimings) {
      return perfTimings;
    }
    const perf: Record<string, string> = {};
    _.each(
      ['x-ms-mte2eelapsedtimems', 'x-ms-odataapie2eelapsedtimems', 'x-ms-odataapionlye2eelapsedtimems'],
      header => {
        const value = response.getResponseHeader(header);
        if (value) {
          perf[header] = value;
        }
      },
    );
    return JSON.stringify(perf);
  },
};

export function beforeSendHandler(
  instrumentationContext: InstrumentationContext,
  request: WrappedRequestor,
  perfFunc: PerformanceLogger,
  shouldAddHeaders: (_context: InstrumentationContext) => boolean,
) {
  instrumentationContext.requestStartTime = getTimestamp();
  if (shouldAddHeaders(instrumentationContext)) {
    request.headers = {
      ...request.headers,
      'x-ms-pagetrackingid': instrumentationContext.pageTrackingId || '',
      'x-ms-lcid': String(instrumentationContext.stubOptions.lcid || ''),
      lcid: String(instrumentationContext.stubOptions.lcid || ''),
      'x-ms-requestid': instrumentationContext.requestId,
    };
  }
  perfFunc({
    requestId: instrumentationContext.requestId,
    api: instrumentationContext.url,
    isMethodEnter: true,
    httpMethod: instrumentationContext.httpMethod,
    timeTaken: 0,
    pass: true,
    message: '',
  });
}

export function successHandler(
  instrumentationContext: InstrumentationContext,
  response: unknown,
  errorFunc: ErrorLogger,
  perfFunc: PerformanceLogger,
) {
  const requestTimeTaken = getTimestamp() - (instrumentationContext.requestStartTime || 0);
  const result = instrumentationContext.stubOptions.detectError(response);
  instrumentationContext.requestTimeTaken = requestTimeTaken;
  if (!result.pass) {
    instrumentationContext.requestResult = false;
    instrumentationContext.error = result.message;
    errorFunc({
      message: instrumentationContext.error,
      api: instrumentationContext.url,
      requestId: instrumentationContext.requestId,
      impactUser: result.impactUser,
      httpMethod: instrumentationContext.httpMethod,
      statusCode: result.status,
    });
  }
  perfFunc({
    requestId: instrumentationContext.requestId,
    api: instrumentationContext.url,
    isMethodEnter: false,
    httpMethod: instrumentationContext.httpMethod,
    timeTaken: instrumentationContext.requestTimeTaken,
    pass: instrumentationContext.requestResult,
    message: instrumentationContext.stubOptions.getServerPerf(response),
    statusCode: 200,
  });
}

export function errorHandler(
  instrumentationContext: InstrumentationContext,
  error: unknown,
  isUserError: (_candidate: unknown) => boolean,
  traceFunc: TraceLogger,
  errorFunc: ErrorLogger,
  perfFunc: PerformanceLogger,
) {
  instrumentationContext.requestTimeTaken =
    getTimestamp() - (instrumentationContext.requestStartTime || 0);
  const httpError = hasHttpErrorDetails(error) ? error : undefined;

  if (httpError?.status && httpError.status !== 0) {
    instrumentationContext.responseJSON = httpError.responseJSON;
    instrumentationContext.requestResult = false;
    instrumentationContext.error =
      `Overreact server response error: [${httpError.status}], error: ${JSON.stringify(error)}`;
    if (httpError.responseJSON) {
      instrumentationContext.error += `, responseJSON: ${errorToString(httpError.responseJSON)}`;
    }
    if ([400, 404, 401].includes(httpError.status)
      && (httpError.status === 401 || isUserError(error))) {
      instrumentationContext.requestResult = true;
    }
  } else {
    instrumentationContext.requestResult = isUserError(error);
    const errMsg = _.isError(error) ? error : JSON.stringify(error);
    instrumentationContext.error = `Overreact general error: ${errMsg}`;
  }

  const logFunc = instrumentationContext.requestResult ? traceFunc : errorFunc;
  logFunc({
    message: instrumentationContext.error,
    api: instrumentationContext.url,
    requestId: instrumentationContext.requestId,
    httpMethod: instrumentationContext.httpMethod,
    statusCode: httpError?.status,
  });
  perfFunc({
    requestId: instrumentationContext.requestId,
    api: instrumentationContext.url,
    isMethodEnter: false,
    httpMethod: instrumentationContext.httpMethod,
    timeTaken: instrumentationContext.requestTimeTaken,
    pass: instrumentationContext.requestResult,
    statusCode: httpError?.status,
  });
}
